import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiCreate, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";

export interface FichaClinica {
  id: string;
  user_id: string;
  paciente_id: string;
  contato_emergencia_nome?: string;
  contato_emergencia_telefone?: string;
  contato_emergencia_parentesco?: string;
  queixa_principal?: string;
  historico_medico?: string;
  alergias?: string;
  pressao_arterial?: string;
  temperatura?: string;
  peso?: string;
  exame_extraoral?: string;
  exame_intraoral?: string;
  diagnostico?: string;
  plano_tratamento?: string;
  observacoes?: string;
  created_at: string;
  updated_at: string;
}

export interface FichaClinicaInput {
  paciente_id: string;
  contato_emergencia_nome?: string;
  contato_emergencia_telefone?: string;
  contato_emergencia_parentesco?: string;
  queixa_principal?: string;
  historico_medico?: string;
  alergias?: string;
  pressao_arterial?: string;
  temperatura?: string;
  peso?: string;
  exame_extraoral?: string;
  exame_intraoral?: string;
  diagnostico?: string;
  plano_tratamento?: string;
  observacoes?: string;
}

export function useFichaClinica(pacienteId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const activeClinicaId = useActiveClinicaId();

  const { data: fichaClinica, isLoading } = useQuery({
    queryKey: ["ficha_clinica", activeClinicaId, pacienteId],
    queryFn: async () => {
      if (!user?.id || !pacienteId) return null;

      const data = await apiList<FichaClinica>("ficha_clinica");
      return data.find((item) => item.paciente_id === pacienteId) || null;
    },
    enabled: !!user?.id && !!pacienteId,
  });

  const saveFichaClinica = useMutation({
    mutationFn: async (input: FichaClinicaInput) => {
      if (!user?.id) throw new Error("User not authenticated");

      const fichas = await apiList<FichaClinica>("ficha_clinica");
      const existing = fichas.find((item) => item.paciente_id === input.paciente_id);
      if (existing) {
        return apiUpdate<FichaClinica>("ficha_clinica", existing.id, input);
      }
      return apiCreate<FichaClinica>("ficha_clinica", input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ficha_clinica"] });
      toast({
        title: "Ficha salva",
        description: "A ficha clínica foi salva com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar a ficha clínica.",
        variant: "destructive",
      });
    },
  });

  return {
    fichaClinica,
    isLoading,
    saveFichaClinica,
  };
}
